import { Col, Row } from "antd";
import { useRouteMatch } from "react-router";
import { Box, Text } from "@chakra-ui/layout";

import TabelaAcordoDivida from "./components/TabelaAcordoDivida";
import { useBreadcrumb } from "features/breadcrumbs";

import useAcordo from "features/acordo/hooks/useAcordo";
import TabelaAcordoParcela from "./components/TabelaAcordoParcela";
import TabelaAcordoEntrada from "./components/TabelaAcordoEntrada";
import Heading from "components/text/heading";
import HeaderUnidade from "components/HeaderUnidade";
import Container from "components/Container";
import { Flex } from "@chakra-ui/react";
import OutlineButton from "ui/components/Button/OutlineButton";
import PrimaryButton from "ui/components/Button/PrimaryButton";
import { formatBlocoQuadra } from "infra/helpers/formatBlocoQuadra";
import { AcordoStatus } from "core/features/acordo/enums";
import history from "infra/helpers/history";
import { useState, useCallback } from "react";
import ModalNovoAcordo from "components/Features/Acordo/NovoAcordo";
import useCobrancaContasComposicoes from "features/cobranca/hooks/useCobrancaContasComposicoes";
import useSaveAcordo from "features/acordo/hooks/useSaveAcordo";
import { showSuccess, showConfirm } from "infra/helpers/alerta";
import { showErrorAxios } from "infra/helpers/alerta";
import TagStatusAcordo from "./../Listagem/components/TagStatus/index";
import moment from "moment";
import useCancelarAcordo from "core/features/acordo/hooks/useCancelarAcordo";
import { useLoading } from "components/GlobalLoading";
import colors from "core/resources/theme/colors";
import ExportMenu from "components/ExportMenu/index";
import { trackEvent } from "infra/helpers/event";
import { ExportarAcordoEvents } from "core/features/acordo/typings";
import { exportExcelAcordo } from "./util/helper";
import useDownloadRenderedPdf from "infra/helpers/useDownloadRenderedPdf";
import PDFAcordo from "./components/PDFAcordo/index";
import { useTenantInfo } from "core/features/relatorios/hooks/useTenantInfo";

function AcordoDetalhesPage() {
  const route = useRouteMatch<{ id: string }>();

  const { data, isLoading, refetch, isFetching } = useAcordo(route.params.id);
  const { mutate: cancelarAcordo, isLoading: isLoadingCancelar } =
    useCancelarAcordo(route.params.id);

  const [isOpenModalAcordo, setIsOpenModalAcordo] = useState(false);

  useBreadcrumb([
    { name: "Financeiro" },
    { name: "Receita" },
    { name: "Acordo", url: "/financeiro/receita/acordo" },
    {
      name: formatBlocoQuadra(data?.unidade.numero, data?.unidade.blocoQuadra),
    },
  ]);

  const parcelasEmAberto =
    data?.parcelas.filter((p) => !p.valorPago && !p.dataRenegociacao) ?? [];

  const { mutate: handleSave, isLoading: isLoadingSave } = useSaveAcordo();

  const showVerNovoAcordo =
    data?.novoAcordoId &&
    [AcordoStatus.Renegociado, AcordoStatus.Encerrado, AcordoStatus.Pago].includes(data?.status) &&
    !isFetching;

  const showCancelarAcordo =
    data?.status === AcordoStatus.EmAberto &&
    !isFetching &&
    data?.parcelas.some(
      (parcela) => !parcela.dataRenegociacao && !parcela.valorPago
    );

  const showRefazerAcordo =
    data?.status === AcordoStatus.EmAberto && !isFetching;

  const { isFetching: isFetchingTenantInfo, refetch: refetchInfo } =
    useTenantInfo(false);

  const { onDownload: onExportPdf } = useDownloadRenderedPdf({
    fileName: `Detalhamento do Acordo - ${data?.codigo || ""}`,
    Component: PDFAcordo,
    loading: isFetchingTenantInfo,
  });

  const _onExportPdf = useCallback(async () => {
    const { data: tenantInfo } = await refetchInfo();

    if (tenantInfo && data) {
      trackEvent(ExportarAcordoEvents.ExportarPDF, data);

      onExportPdf({
        ...tenantInfo,
        ...data,
      });
    }
  }, [data, onExportPdf, refetchInfo]);

  const _onExportExcel = useCallback(async () => {
    if (!data) return;

    trackEvent(ExportarAcordoEvents.ExportarExcel, data);
    exportExcelAcordo({
      data,
    });
  }, [data]);

  useLoading(isLoadingCancelar);

  return (
    <Container pb="32px">
      <Row gutter={32}>
        <Col sm={24}>
          <Heading mb="32px">
            <Flex gap={2}>
              Detalhamento do Acordo{" "}
              {data?.codigo ? ` - ${data.codigo} - ` : "-"}
              <Flex gridGap="8px" alignItems="center" flexDir="row">
                <TagStatusAcordo
                  width={"max-content"}
                  px="30px"
                  status={data?.status!}
                  dataCancelamento={data?.dataCancelamento}
                />

                {data?.dataPagamento ? (
                  <Text>
                    Pago em {moment(data?.dataPagamento).format("DD/MM/YYYY")}
                  </Text>
                ) : null}

                {data?.diasEmAtraso ? (
                  <Text color="red">
                    Com atraso de {data?.diasEmAtraso} dia
                    {data?.diasEmAtraso === 1 ? "" : "s"}
                  </Text>
                ) : null}
              </Flex>
            </Flex>
          </Heading>
          <Box>
            <HeaderUnidade
              bloco={data?.unidade.blocoQuadra}
              nome={data?.morador?.nome!}
              unidade={data?.unidade.numero!}
            />
          </Box>
        </Col>
      </Row>

      <Box mt="24px">
        <Flex mb="16px" gap={4}>
          <Text fontSize="16px" fontWeight="bold" mr="auto">
            Resumo das parcelas do acordo
          </Text>
          {showVerNovoAcordo ? (
            <OutlineButton
              onClick={() => {
                history.push(`/financeiro/receita/acordo/${data.novoAcordoId}`);
              }}
            >
              Ver novo acordo
            </OutlineButton>
          ) : null}

          {data ? (
            <ExportMenu
              onExportPdf={_onExportPdf}
              onExportarXlsx={_onExportExcel}
            />
          ) : null}

          {showCancelarAcordo ? (
            <OutlineButton
              onClick={() => {
                showConfirm("Deseja realmente cancelar esse acordo?").then(
                  ({ isConfirmed }) => {
                    if (isConfirmed)
                      cancelarAcordo(undefined, {
                        onSuccess: () => refetch(),
                      });
                  }
                );
              }}
              disabled={!parcelasEmAberto?.length}
              color={colors.red500}
              borderColor={colors.red500}
            >
              Cancelar acordo
            </OutlineButton>
          ) : null}

          {showRefazerAcordo ? (
            <PrimaryButton
              onClick={() => setIsOpenModalAcordo(true)}
              disabled={!parcelasEmAberto?.length}
              title={
                !parcelasEmAberto?.length
                  ? "Não é possível refazer acordo que não tem parcelas em aberto"
                  : undefined
              }
            >
              Refazer acordo
            </PrimaryButton>
          ) : null}
        </Flex>
        <Row gutter={16}>
          <Col sm={8}>
            <TabelaAcordoEntrada
              data={data?.entrada!}
              isLoading={isLoading || isFetching}
            />
          </Col>

          <Col sm={16}>
            <TabelaAcordoParcela
              data={data?.parcelas ?? []}
              isLoading={isLoading || isFetching}
            />
          </Col>
        </Row>
      </Box>
      <Box>
        <Row gutter={32}>
          <Col sm={24}>
            <TabelaAcordoDivida
              data={data?.dividasAcordo ?? []}
              isLoading={isLoading}
            />
          </Col>
        </Row>
      </Box>

      {isOpenModalAcordo && data ? (
        <ModalNovoAcordo
          unidadeId={data?.unidade.id}
          isOpen={isOpenModalAcordo}
          onClose={() => {
            setIsOpenModalAcordo(false);
          }}
          fetchComposicoesHook={useCobrancaContasComposicoes}
          fetchComposicoesHookParams={{
            unidadeId: data?.unidade.id,
            contas: parcelasEmAberto.map((parcela) => parcela.id),
          }}
          isLoadingSave={isLoadingSave}
          onSubmit={(data) => {
            handleSave(
              {
                ...data,
                contas: parcelasEmAberto.map((parcela) => parcela.id),
              },
              {
                onSuccess: async () => {
                  await showSuccess();
                  setIsOpenModalAcordo(false);
                  refetch();
                },
                onError: (err: any) => {
                  showErrorAxios(err);
                },
              }
            );
          }}
        />
      ) : null}
    </Container>
  );
}

export default AcordoDetalhesPage;
